import { useState, useEffect } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase/firebase";

// Custom hook for fetching career opportunities from Firestore
export const useFirestoreData = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  // Collection names that correspond to the original JSON file types
  const COLLECTIONS = [
    "apprenticeships",
    "conferences",
    "courses",
    "events",
    "fellowships",
    "government-programs",
    "graduate-level",
    "internships",
    "programs",
    "professional-development-programs",
    "professional-organizations",
    "research",
    "scholarships",
    "volunteers",
  ];

  const fetchData = async (forceRefresh = false) => {
    // If data exists and was fetched recently (within 5 minutes), don't refetch unless forced
    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
    if (
      !forceRefresh &&
      data.length > 0 &&
      lastFetch &&
      lastFetch > fiveMinutesAgo
    ) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const allData = [];

      // Fetch data from all collections
      for (const collectionName of COLLECTIONS) {
        try {
          const collectionRef = collection(db, collectionName);
          const q = query(collectionRef, orderBy("name", "asc"));
          const querySnapshot = await getDocs(q);

          querySnapshot.forEach((doc) => {
            const docData = doc.data();
            // Add the document ID and collection type for reference
            allData.push({
              id: doc.id,
              sourceCollection: collectionName,
              ...docData,
            });
          });
        } catch (collectionError) {
          console.warn(
            `Failed to fetch from collection ${collectionName}:`,
            collectionError
          );
          // Continue with other collections even if one fails
        }
      }

      setData(allData);
      setLastFetch(Date.now());
      setError(null);
    } catch (err) {
      console.error("Error fetching data from Firestore:", err);
      setError({
        message: "Failed to load career opportunities",
        details: err.message,
        code: err.code || "unknown",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Return data and utility functions
  return {
    data,
    loading,
    error,
    refetch: () => fetchData(true),
    lastFetch: lastFetch ? new Date(lastFetch) : null,
  };
};

// Hook for fetching data from a specific collection
export const useFirestoreCollection = (collectionName) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCollectionData = async () => {
      setLoading(true);
      setError(null);

      try {
        const collectionRef = collection(db, collectionName);
        const q = query(collectionRef, orderBy("name", "asc"));
        const querySnapshot = await getDocs(q);

        const collectionData = [];
        querySnapshot.forEach((doc) => {
          collectionData.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        setData(collectionData);
      } catch (err) {
        console.error(`Error fetching ${collectionName} collection:`, err);
        setError({
          message: `Failed to load ${collectionName}`,
          details: err.message,
          code: err.code || "unknown",
        });
      } finally {
        setLoading(false);
      }
    };

    if (collectionName) {
      fetchCollectionData();
    }
  }, [collectionName]);

  return { data, loading, error };
};

export default useFirestoreData;
